"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useNav = exports.flattenNavs = exports.getNavPathname = exports.processPage = void 0;
const doNothing_1 = __importDefault(require("@opsbase/lib-general/utils/doNothing"));
const lib_i18n_1 = __importDefault(require("@opsbase/lib-i18n"));
const lib_pages_1 = __importDefault(require("@opsbase/lib-pages"));
const lib_palette_1 = __importDefault(require("@opsbase/lib-palette"));
const useNotificationActions_1 = __importDefault(require("@opsbase/web-notification/useNotificationActions"));
const flatten_1 = __importDefault(require("lodash/flatten"));
const react_1 = require("react");
const react_router_dom_1 = require("react-router-dom");
const useParams_1 = require("../useParams");
const processPage = (page, params = {}, pathname = '') => {
    let url = page;
    url = url.replace('*', pathname);
    Object.keys(params).forEach(k => (url = url.replace(':' + k, params[k])));
    return url;
};
exports.processPage = processPage;
function getNavPathname(nav) {
    return nav.pathname || nav.link;
}
exports.getNavPathname = getNavPathname;
function flattenNavs(navs) {
    if (!(navs === null || navs === void 0 ? void 0 : navs.length))
        return [];
    return (0, flatten_1.default)(navs.map(nav => [nav, ...flattenNavs(nav.children), ...flattenNavs(nav.subnavs)]));
}
exports.flattenNavs = flattenNavs;
const hasUnsaved = {
    current: false,
};
const doSave = {
    current: doNothing_1.default,
};
function useNav() {
    const { pathname } = (0, react_router_dom_1.useLocation)();
    const history = (0, react_router_dom_1.useHistory)();
    const { raiseOkCancel } = (0, useNotificationActions_1.default)();
    const localHistory = (0, react_1.useRef)(history);
    const localPathname = (0, react_1.useRef)(pathname);
    (0, react_1.useEffect)(() => {
        localPathname.current = pathname;
    }, [pathname]);
    (0, react_1.useEffect)(() => {
        localHistory.current = history;
    }, [history]);
    const setUnsavedWarning = (0, react_1.useMemo)(() => (onOff) => {
        hasUnsaved.current = onOff;
    }, []);
    const setDoSave = (0, react_1.useMemo)(() => (savefunction) => {
        doSave.current = savefunction;
    }, []);
    const doVerify = (0, react_1.useMemo)(() => (onOk) => {
        if (!!hasUnsaved.current)
            raiseOkCancel(lib_i18n_1.default.t('you-have-unsaved-changes'), {
                okLabel: lib_i18n_1.default.t('save-changes'),
                onOk: () => {
                    var _a;
                    hasUnsaved.current = false;
                    (_a = doSave.current) === null || _a === void 0 ? void 0 : _a.call(doSave, false);
                    onOk();
                },
                cancelLabel: lib_i18n_1.default.t('discard-changes'),
                cancelColor: lib_palette_1.default.error,
                onCancel: () => {
                    hasUnsaved.current = false;
                    onOk();
                },
            });
        else
            onOk();
    }, [raiseOkCancel]);
    const _doGoTo = (0, react_1.useMemo)(() => (page, urlParams = {}, navParams = {}) => {
        useParams_1._navParams.current = navParams;
        localHistory.current.push((0, exports.processPage)(page, urlParams, localPathname.current));
    }, []);
    const goTo = (0, react_1.useMemo)(() => (page, urlParams, navParams) => {
        doVerify(() => _doGoTo(page, urlParams, navParams));
    }, [_doGoTo, doVerify]);
    const _doReplaceTo = (0, react_1.useMemo)(() => (page, urlParams = {}, navParams = {}) => {
        useParams_1._navParams.current = navParams;
        localHistory.current.replace((0, exports.processPage)(page, urlParams, localPathname.current));
    }, []);
    const replaceTo = (0, react_1.useMemo)(() => (page, urlParams, navParams) => {
        doVerify(() => _doReplaceTo(page, urlParams, navParams));
    }, [doVerify, _doReplaceTo]);
    const _doGoBack = (0, react_1.useMemo)(() => () => {
        if (window.history.length > 1) {
            localHistory.current.goBack();
        }
        else {
            goTo(lib_pages_1.default.TODO);
        }
    }, [goTo]);
    const goBack = (0, react_1.useMemo)(() => () => doVerify(() => _doGoBack()), [_doGoBack, doVerify]);
    const openTo = (0, react_1.useMemo)(() => (page, urlParams, target = '_blank') => {
        window.open('/#' + (0, exports.processPage)(page, urlParams, localPathname.current), target);
    }, []);
    const openUrl = (0, react_1.useMemo)(() => url => {
        window.open(url);
    }, []);
    const isAMatch = (0, react_1.useMemo)(() => path => (0, react_router_dom_1.matchPath)(pathname, { path }), [pathname]);
    const isSelected = (0, react_1.useMemo)(() => (nav) => (0, react_router_dom_1.matchPath)(pathname, {
        path: getNavPathname(nav),
        exact: false,
        strict: false,
    }), [pathname]);
    const getCurrent = (0, react_1.useMemo)(() => (navs) => {
        const matches = flattenNavs(navs)
            .map(nav => ({ match: (0, react_router_dom_1.matchPath)(pathname, { path: getNavPathname(nav) }), nav }))
            .filter(m => !!(m === null || m === void 0 ? void 0 : m.match));
        // No matches, no current
        if (!matches.length)
            return undefined;
        // If there is an exact match, thats our current Nav
        const exact = matches.find(m => !!m.match.isExact);
        if (exact)
            return exact.nav;
        // Otherwise, its the match with the longest pathname
        matches.sort((a, b) => b.match.path.length - a.match.path.length);
        return matches[0].nav;
    }, [pathname]);
    return {
        goTo,
        replaceTo,
        goBack,
        openTo,
        openUrl,
        pathname,
        isAMatch,
        isSelected,
        getCurrent,
        setUnsavedWarning,
        setDoSave,
    };
}
exports.useNav = useNav;
exports.default = useNav;
